import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Headphones, Sliders, Radio, Monitor, Cable, Volume2, Music, PlayCircle, FileText, CheckCircle2, XCircle, Clock, ShieldCheck, Download, ChevronDown, ChevronUp, CreditCard, Mail, Zap, Users, Facebook, Instagram } from 'lucide-react';
// Countdown Timer Hook
const useCountdown = (initialHours: number = 2) => {
  const [timeLeft, setTimeLeft] = useState(initialHours * 60 * 60);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0')
  };
};
// Floating Icon Component
const FloatingIcon = ({
  delay,
  x,
  size,
  Icon
}: {
  delay: number;
  x: string;
  size: number;
  Icon: React.ElementType;
}) => <motion.div initial={{
  y: 100,
  opacity: 0
}} animate={{
  y: -100,
  opacity: [0, 0.3, 0],
  rotate: [0, 20, -20, 0]
}} transition={{
  duration: 8,
  repeat: Infinity,
  delay: delay,
  ease: 'linear'
}} className="absolute text-[#FF8719] pointer-events-none select-none z-0" style={{
  left: x,
  width: size,
  height: size
}} data-id="element-1">
    <Icon size={size} data-id="element-2" />
  </motion.div>;
// FAQ Item Component
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-700 last:border-0" data-id="element-3">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-4 flex items-center justify-between text-left focus:outline-none group" data-id="element-4">
        <span className="font-bold text-[#E2E6E9] group-hover:text-[#FF8719] transition-colors font-sans text-lg" data-id="element-5">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-[#FF8719]" data-id="element-6" /> : <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-[#FF8719]" data-id="element-7" />}
      </button>
      <AnimatePresence data-id="element-8">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-9">
            <p className="pb-4 text-gray-400 leading-relaxed font-sans" data-id="element-10">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
// Purchase Notification Popup Component
const PurchaseNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentBuyer, setCurrentBuyer] = useState({
    name: '',
    province: '',
    minutes: 0
  });
  const buyers = [{
    name: 'Carlos M.',
    province: 'Resistencia, Chaco'
  }, {
    name: 'María L.',
    province: 'Córdoba, Córdoba'
  }, {
    name: 'Juan P.',
    province: 'Rosario, Santa Fe'
  }, {
    name: 'Luciana G.',
    province: 'Buenos Aires, CABA'
  }, {
    name: 'Martín R.',
    province: 'Mendoza, Mendoza'
  }, {
    name: 'Sofía B.',
    province: 'La Plata, Buenos Aires'
  }, {
    name: 'Diego F.',
    province: 'San Miguel de Tucumán'
  }, {
    name: 'Valentina S.',
    province: 'Mar del Plata, Buenos Aires'
  }, {
    name: 'Nicolás A.',
    province: 'Salta, Salta'
  }, {
    name: 'Camila T.',
    province: 'Neuquén, Neuquén'
  }, {
    name: 'Federico H.',
    province: 'Paraná, Entre Ríos'
  }, {
    name: 'Agustina M.',
    province: 'San Juan, San Juan'
  }, {
    name: 'Tomás V.',
    province: 'Corrientes, Corrientes'
  }, {
    name: 'Florencia D.',
    province: 'Bahía Blanca, Buenos Aires'
  }, {
    name: 'Sebastián C.',
    province: 'Santa Rosa, La Pampa'
  }, {
    name: 'Julieta R.',
    province: 'Posadas, Misiones'
  }, {
    name: 'Matías L.',
    province: 'San Luis, San Luis'
  }, {
    name: 'Rocío P.',
    province: 'Rawson, Chubut'
  }, {
    name: 'Gonzalo E.',
    province: 'Río Gallegos, Santa Cruz'
  }, {
    name: 'Antonella K.',
    province: 'Ushuaia, Tierra del Fuego'
  }];
  useEffect(() => {
    const showNotification = () => {
      const randomBuyer = buyers[Math.floor(Math.random() * buyers.length)];
      const randomMinutes = Math.floor(Math.random() * 30) + 1;
      setCurrentBuyer({
        ...randomBuyer,
        minutes: randomMinutes
      });
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 4000);
    };
    // Initial delay
    const initialTimeout = setTimeout(showNotification, 5000);
    // Recurring notifications
    const interval = setInterval(showNotification, 15000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
  return <AnimatePresence data-id="element-11">
      {isVisible && <motion.div initial={{
      x: -400,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: -400,
      opacity: 0
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="fixed bottom-24 sm:bottom-28 left-4 sm:left-6 z-40 bg-[#1D1F22] rounded-xl shadow-2xl p-4 max-w-xs border border-gray-700" data-id="element-12">
          <div className="flex items-start gap-3" data-id="element-13">
            <div className="bg-green-500 rounded-full p-1" data-id="element-14">
              <CheckCircle2 className="w-5 h-5 text-white" data-id="element-15" />
            </div>
            <div className="flex-1" data-id="element-16">
              <p className="font-bold text-white text-sm" data-id="element-17">
                {currentBuyer.name}
              </p>
              <p className="text-gray-400 text-xs flex items-center gap-1" data-id="element-18">
                <span data-id="element-19">📍</span> {currentBuyer.province}
              </p>
              <p className="text-[#FF8719] text-sm mt-1" data-id="element-20">
                Compró <span className="font-bold" data-id="element-21">Guía - Home Studio</span>
              </p>
              <p className="text-gray-500 text-xs mt-1" data-id="element-22">
                Hace {currentBuyer.minutes} min • Verificado ✓
              </p>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
export function LandingPage() {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown(24);
  const painPoints = [{
    title: 'Demasiada información desordenada',
    desc: "Hoy hay demasiada info (reviews, marcas, 'lo mejor') y casi nada de orden."
  }, {
    title: 'Comprar sin saber usar',
    desc: 'El error más común es comprar equipo sin saber usarlo (y después frustrarse con el audio).'
  }, {
    title: 'Falta de un camino claro',
    desc: 'No te falta talento: te falta un camino simple para decidir qué comprar y cómo configurarlo.'
  }, {
    title: 'Inversión sin criterio',
    desc: 'Si vas a invertir, que sea con criterio (y no por impulso).'
  }];
  const outcomes = ['Elegir micrófono e interfaz sin adivinar (según lo que vas a grabar)', 'Armar un setup simple y funcional en tu casa (sin "estudio perfecto")', 'Configurar Ableton con lo esencial: entradas/salidas, master, envíos', 'Grabar tu primera toma con niveles correctos, reverb/delay básicos', 'Evitar compras innecesarias (y gastar donde sí hace diferencia)'];
  const bonuses = [{
    id: 1,
    title: 'Checklist de compra por presupuesto (USD)',
    desc: 'Organiza tu inversión según tu presupuesto disponible',
    price: '$9.900',
    icon: <CreditCard className="w-6 h-6 text-[#FF8719]" data-id="element-23" />
  }, {
    id: 2,
    title: 'Guía de errores técnicos caros',
    desc: 'Evita los errores que te cuestan tiempo y dinero',
    price: '$8.900',
    icon: <XCircle className="w-6 h-6 text-[#FF8719]" data-id="element-24" />
  }, {
    id: 3,
    title: 'Checklist para elegir tu computadora',
    desc: 'Elige la PC o Mac ideal para producción musical',
    price: '$12.900',
    icon: <Monitor className="w-6 h-6 text-[#FF8719]" data-id="element-25" />
  }, {
    id: 4,
    title: 'Glosario completo de Home Studio',
    desc: 'Todos los términos que necesitas conocer',
    price: '$10.900',
    icon: <FileText className="w-6 h-6 text-[#FF8719]" data-id="element-26" />
  }, {
    id: 5,
    title: 'Aspectos Generales de Ableton Live',
    desc: 'Video tutorial con lo esencial del DAW',
    price: '$14.900',
    icon: <PlayCircle className="w-6 h-6 text-[#FF8719]" data-id="element-27" />
  }, {
    id: 6,
    title: 'Guía práctica de frecuencias',
    desc: 'Entiende el espectro de frecuencias para mezclar mejor',
    price: '$7.900',
    icon: <Sliders className="w-6 h-6 text-[#FF8719]" data-id="element-28" />
  }, {
    id: 7,
    title: 'Pensar como productor',
    desc: 'Mentalidad y workflow profesional',
    price: '$16.900',
    icon: <Headphones className="w-6 h-6 text-[#FF8719]" data-id="element-29" />
  }];
  const faqs = [{
    question: '¿Necesito conocimientos previos?',
    answer: 'No. Está pensado para principiantes y te lleva de cero a tu primer grabación funcional.'
  }, {
    question: '¿Sirve si uso Logic o Pro Tools?',
    answer: 'Sí. La guía se enfoca en decisiones y conceptos (flujo, niveles, monitoreo). La clase extra es en Ableton, pero el criterio te sirve igual.'
  }, {
    question: '¿Qué pasa si ya tengo micrófono o interfaz?',
    answer: 'También sirve: vas a ordenar el setup, configuración y el "qué hago ahora" para grabar mejor con lo que ya tenés.'
  }, {
    question: '¿Cuánto tiempo me lleva?',
    answer: 'En 40–60 minutos entendés el mapa completo. Luego lo aplicás con el plan de 7 días para avanzar sin trabarte.'
  }, {
    question: '¿Cómo recibo la guía y los bonos?',
    answer: 'Por email, con acceso inmediato (PDF + materiales). Guardalos en Drive/Notas para tenerlos a mano.'
  }];
  return <div className="min-h-screen bg-[#1D1F22] text-[#E2E6E9] overflow-hidden relative font-sans selection:bg-[#FF8719] selection:text-white" data-id="element-30">
      {/* Background Decorations */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0" data-id="element-31">
        <FloatingIcon delay={0} x="10%" size={40} Icon={Mic} data-id="element-32" />
        <FloatingIcon delay={2} x="80%" size={60} Icon={Headphones} data-id="element-33" />
        <FloatingIcon delay={1} x="20%" size={30} Icon={Sliders} data-id="element-34" />
        <FloatingIcon delay={3} x="90%" size={50} Icon={Radio} data-id="element-35" />
        <FloatingIcon delay={4} x="5%" size={45} Icon={Monitor} data-id="element-36" />
        <FloatingIcon delay={1.5} x="70%" size={35} Icon={Volume2} data-id="element-37" />
        <FloatingIcon delay={5} x="40%" size={55} Icon={Cable} data-id="element-38" />
      </div>

      {/* Top Sticky Banner */}
      <div className="bg-[#FF8719] text-white py-3 px-4 sticky top-0 z-50 shadow-lg" data-id="element-39">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 text-center" data-id="element-40">
          <motion.div animate={{
          scale: [1, 1.05, 1]
        }} transition={{
          repeat: Infinity,
          duration: 2
        }} className="font-bold text-lg flex items-center gap-2 font-sans" data-id="element-41">
            <span className="bg-white text-[#FF8719] px-2 py-0.5 rounded text-sm font-extrabold" data-id="element-42">
              50% OFF
            </span>
            <span data-id="element-43">+ 7 BONOS GRATIS HOY</span>
          </motion.div>

          <div className="flex items-center gap-3" data-id="element-44">
            <div className="flex items-center gap-2 font-mono bg-black/20 px-4 py-1 rounded-full" data-id="element-45">
              <Clock className="w-4 h-4" data-id="element-46" />
              <span className="font-bold tracking-widest text-lg" data-id="element-47">
                {hours}:{minutes}:{seconds}
              </span>
            </div>
            <a href="https://www.institutoclave.com/cart/47548550971610:1" target="_blank" rel="noopener noreferrer" className="hidden sm:flex bg-white text-[#FF8719] font-bold px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm items-center gap-2 shadow-md" data-id="element-48">
              <Download className="w-4 h-4" data-id="element-49" />
              Obtener acceso
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Fixed CTA Bar - Mobile & Desktop */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1D1F22]/95 backdrop-blur-sm border-t border-gray-700 p-3 shadow-2xl" data-id="element-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4" data-id="element-51">
          <div className="hidden sm:flex flex-col" data-id="element-52">
            <span className="text-gray-400 text-xs font-sans" data-id="element-53">
              Precio especial
            </span>
            <div className="flex items-center gap-2" data-id="element-54">
              <span className="text-gray-500 line-through text-sm font-sans" data-id="element-55">
                $27.980
              </span>
              <span className="text-[#FF8719] font-bold text-xl font-sans" data-id="element-56">
                $13.990 ARS
              </span>
            </div>
          </div>
          <a href="https://www.institutoclave.com/cart/47548550971610:1" target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none bg-[#FF8719] hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-xl transition-all text-center flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 font-sans" data-id="element-57">
            <Download className="w-5 h-5" data-id="element-58" />
            <span data-id="element-59">Obtener acceso ahora</span>
          </a>
          <div className="hidden md:flex items-center gap-2 text-gray-400 text-xs font-sans" data-id="element-60">
            <ShieldCheck className="w-4 h-4 text-green-400" data-id="element-61" />
            <span data-id="element-62">Garantía 7 días</span>
          </div>
        </div>
      </div>

      <main className="relative z-10" data-id="element-63">
        {/* Hero Section */}
        <section className="pt-6 pb-12 md:pb-20 px-4 text-center max-w-5xl mx-auto" data-id="element-64">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="inline-flex items-center gap-2 bg-[#2A2D31] border border-gray-700 px-4 py-2 rounded-full mb-3" data-id="element-65">
            <Zap className="w-4 h-4 text-[#FF8719] fill-current" data-id="element-66" />
            <span className="text-[#E2E6E9] font-bold text-sm font-sans" data-id="element-67">
              Guía + 7 Bonos + Clase Gratis
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-6xl text-white mb-4 leading-tight tracking-wide" data-id="element-68">
            Cómo armar tu{' '}
            <span className="text-[#FF8719] relative inline-block" data-id="element-69">
              home studio
            </span>{' '}
            <span className="relative inline-block" data-id="element-70">
              desde cero
              <svg className="absolute w-full h-4 -bottom-1 left-0" viewBox="0 0 200 12" preserveAspectRatio="none" data-id="element-71">
                <path d="M2 8 Q 40 2, 80 7 T 160 6 T 198 8" stroke="#FF8719" strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-72" />
              </svg>
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-lg md:text-2xl text-gray-400 mb-6 max-w-3xl mx-auto font-sans font-light" data-id="element-73">
            La guía que me hubiera gustado tener al empezar: orden, criterio y
            pasos claros para no gastar plata de más ni perder meses.
          </motion.p>

          {/* Product Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.25,
          duration: 0.5
        }} className="mb-6 flex justify-center" data-id="element-74">
            <div className="relative group" data-id="element-75">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#FF8719] to-orange-600 rounded-lg blur opacity-10 group-hover:opacity-20 transition duration-1000 group-hover:duration-200" data-id="element-76"></div>
              <motion.img src="https://cdn.magicpatterns.com/uploads/pMUefni6jvweNreiZQguUv/Como_armar_tu_Home_Studio_desde_cero.png" alt="Home Studio Guide Product Mockup" className="relative rounded-lg shadow-2xl w-full max-w-[550px] h-auto" animate={{
              y: [0, -15, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-77" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="flex flex-col items-center justify-center gap-3" data-id="element-78">
            <a href="https://www.institutoclave.com/cart/47548550971610:1" target="_blank" rel="noopener noreferrer" className="bg-[#FF8719] hover:bg-orange-600 text-white text-base md:text-lg font-bold py-3 px-6 md:py-4 md:px-8 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 font-sans" data-id="element-79">
              👉 Quiero armar mi home studio con orden
            </a>
            <a href="#bonuses" className="text-[#FF8719] font-semibold hover:text-orange-400 flex items-center gap-1 font-sans" data-id="element-80">
              Ver bonos incluidos <ChevronDown className="w-4 h-4" data-id="element-81" />
            </a>
          </motion.div>
        </section>

        {/* Why This Guide Exists (Pain Points) */}
        <section className="bg-[#2A2D31] py-12 md:py-20 px-4" data-id="element-82">
          <div className="max-w-6xl mx-auto" data-id="element-83">
            <div className="text-center mb-8 md:mb-16" data-id="element-84">
              <span className="text-3xl md:text-4xl mb-2 block" data-id="element-85">🧩</span>
              <h2 className="text-2xl md:text-4xl text-white" data-id="element-86">
                ¿Por qué esta guía existe?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-87">
              {painPoints.map((point, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-[#1D1F22] p-5 md:p-8 rounded-2xl shadow-lg border border-gray-800 hover:border-[#FF8719]/30 transition-colors" data-id="element-88">
                  <div className="bg-red-500/10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-3" data-id="element-89">
                    <XCircle className="w-5 h-5 md:w-6 md:h-6 text-red-500" data-id="element-90" />
                  </div>
                  <h3 className="text-lg md:text-xl text-white mb-2 font-sans font-bold" data-id="element-91">
                    {point.title}
                  </h3>
                  <p className="text-gray-400 font-sans text-sm md:text-base" data-id="element-92">
                    {point.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* What You'll Achieve */}
        <section className="py-12 md:py-20 px-4 relative overflow-hidden" data-id="element-93">
          <div className="max-w-5xl mx-auto" data-id="element-94">
            <div className="text-center mb-8 md:mb-16" data-id="element-95">
              <span className="text-3xl md:text-4xl mb-2 block" data-id="element-96">🎯</span>
              <h2 className="text-2xl md:text-4xl text-white mb-2 md:mb-4" data-id="element-97">
                Lo que vas a lograr
              </h2>
            </div>

            <div className="bg-gradient-to-br from-[#2A2D31] to-[#1D1F22] rounded-2xl md:rounded-3xl p-5 md:p-12 border border-gray-800 shadow-2xl relative overflow-hidden" data-id="element-98">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF8719] opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-99"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 opacity-5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-100"></div>

              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center relative z-10" data-id="element-101">
                <div className="space-y-4 md:space-y-6" data-id="element-102">
                  {outcomes.map((outcome, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1
                }} className="flex items-start gap-3" data-id="element-103">
                      <div className="bg-[#FF8719]/20 p-1 rounded-full mt-1" data-id="element-104">
                        <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#FF8719]" data-id="element-105" />
                      </div>
                      <span className="text-base md:text-lg text-gray-300 font-medium font-sans" data-id="element-106">
                        {outcome}
                      </span>
                    </motion.div>)}
                </div>
                <div className="relative flex justify-center hidden md:flex" data-id="element-107">
                  <div className="bg-[#1D1F22] p-6 rounded-xl shadow-2xl border border-gray-700 w-full max-w-sm transform rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-108">
                    <div className="flex items-center justify-between mb-6 border-b border-gray-700 pb-4" data-id="element-109">
                      <div className="flex items-center gap-2" data-id="element-110">
                        <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-111"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-112"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-113"></div>
                      </div>
                      <span className="text-xs text-gray-500 font-mono" data-id="element-114">
                        Ableton Live
                      </span>
                    </div>
                    <div className="space-y-3" data-id="element-115">
                      <div className="h-2 bg-gray-700 rounded w-3/4" data-id="element-116"></div>
                      <div className="h-2 bg-gray-700 rounded w-1/2" data-id="element-117"></div>
                      <div className="mt-4 flex gap-2" data-id="element-118">
                        {[1, 2, 3, 4].map(bar => <div key={bar} className="h-16 w-full bg-[#FF8719]/20 rounded-sm flex items-end pb-1 justify-center" data-id="element-119">
                            <div className="w-2 bg-[#FF8719] rounded-sm" style={{
                          height: `${Math.random() * 100}%`
                        }} data-id="element-120"></div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="bg-[#2A2D31] py-12 md:py-20 px-4" data-id="element-121">
          <div className="max-w-6xl mx-auto" data-id="element-122">
            <div className="text-center mb-8 md:mb-16" data-id="element-123">
              <span className="text-3xl md:text-4xl mb-2 block" data-id="element-124">📦</span>
              <h2 className="text-2xl md:text-4xl text-white mb-2 md:mb-4" data-id="element-125">
                Qué incluye
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-126">
              {/* Card 1: The Guide */}
              <motion.div whileHover={{
              y: -5
            }} className="bg-[#1D1F22] p-5 md:p-8 rounded-2xl border border-gray-800 shadow-lg hover:border-[#FF8719]/30 transition-all" data-id="element-127">
                <div className="bg-[#FF8719]/10 w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-4 md:mb-6 text-[#FF8719]" data-id="element-128">
                  <FileText className="w-6 h-6 md:w-8 md:h-8" data-id="element-129" />
                </div>
                <h3 className="text-xl md:text-2xl text-white mb-3 md:mb-4 font-sans font-bold" data-id="element-130">
                  📘 La guía (PDF)
                </h3>
                <ul className="space-y-2 md:space-y-3 text-gray-400 font-sans text-sm md:text-base" data-id="element-131">
                  <li className="flex items-start gap-2" data-id="element-132">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#FF8719] mt-0.5 flex-shrink-0" data-id="element-133" />
                    <span data-id="element-134">Ruta paso a paso para armar tu home studio.</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-135">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#FF8719] mt-0.5 flex-shrink-0" data-id="element-136" />
                    <span data-id="element-137">
                      Decisiones claras: qué comprar primero y qué dejar.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-138">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#FF8719] mt-0.5 flex-shrink-0" data-id="element-139" />
                    <span data-id="element-140">Checklist de errores y ajustes rápidos.</span>
                  </li>
                </ul>
              </motion.div>

              {/* Card 2: Free Class */}
              <motion.div whileHover={{
              y: -5
            }} className="bg-[#1D1F22] p-5 md:p-8 rounded-2xl border border-gray-800 shadow-lg relative overflow-hidden hover:border-[#FF8719]/-30 transition-all" data-id="element-141">
                <div className="absolute top-3 right-3 md:top-4 md:right-4 bg-green-900/50 text-green-400 px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs font-bold border border-green-800" data-id="element-142">
                  GRATIS
                </div>
                <div className="bg-[#FF8719]/10 w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center mb-4 md:mb-6 text-[#FF8719]" data-id="element-143">
                  <PlayCircle className="w-6 h-6 md:w-8 md:h-8" data-id="element-144" />
                </div>
                <h3 className="text-xl md:text-2xl text-white mb-3 md:mb-4 font-sans font-bold" data-id="element-145">
                  🎛️ Clase: Ableton básico
                </h3>
                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6 text-gray-400 font-sans text-sm md:text-base" data-id="element-146">
                  <li className="flex items-start gap-2" data-id="element-147">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#FF8719] mt-0.5 flex-shrink-0" data-id="element-148" />
                    <span data-id="element-149">Vista general, canales y master.</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-150">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-[#FF8719] mt-0.5 flex-shrink-0" data-id="element-151" />
                    <span data-id="element-152">Envíos/retornos para sonar mejor rápido.</span>
                  </li>
                </ul>
                <p className="text-xs md:text-sm text-gray-500 italic bg-[#2A2D31] p-2 md:p-3 rounded-lg border border-gray-700 font-sans" data-id="element-153">
                  *La idea es que puedas replicarlo también en Logic/Pro Tools:
                  los conceptos son universales.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* About the Author - Santiago Peralta */}
        <section className="py-12 md:py-20 px-4 bg-[#1D1F22]" data-id="element-154">
          <div className="max-w-5xl mx-auto" data-id="element-155">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-156">
              {/* Image Column */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="flex justify-center md:justify-start" data-id="element-157">
                <div className="relative" data-id="element-158">
                  <div className="absolute -inset-2 bg-gradient-to-r from-[#FF8719]/20 to-orange-600/20 rounded-full blur-xl" data-id="element-159"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/oFiCZQWqVXAZjcR2CPo2zy/Santiago_Peralta.jpg" alt="Santiago Peralta" className="relative w-72 h-72 md:w-96 md:h-96 lg:w-[420px] lg:h-[420px] rounded-full object-cover border-4 border-gray-700 shadow-2xl" data-id="element-160" />
                </div>
              </motion.div>

              {/* Text Column */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="space-y-4 md:space-y-5" data-id="element-161">
                <p className="text-gray-300 font-sans text-sm md:text-base leading-relaxed" data-id="element-162">
                  Hola, soy{' '}
                  <span className="text-white font-semibold" data-id="element-163">
                    Santiago Peralta
                  </span>
                  , músico y productor. Como muchos, empecé grabando con lo que
                  había, aprendiendo a prueba y error. Con el tiempo entendí que
                  mi home studio no es solo un lugar con equipos, sino mi
                  espacio de inspiración: un lugar al que quiero llegar y que
                  todo esté listo cuando aparece una idea, sin que lo técnico me
                  frene.
                </p>

                <p className="text-gray-300 font-sans text-sm md:text-base leading-relaxed" data-id="element-164">
                  Durante años busqué información por todos lados y me encontré
                  con lo mismo: mucho tecnicismo y poca claridad. Ahí entendí
                  que no sirve saber de todo si no sabés qué es realmente
                  importante. Desde ese lugar empecé a aprender y a ordenar la
                  información, pensando siempre en estudios reales, caseros,
                  como el que tenemos la mayoría.
                </p>

                <p className="text-gray-300 font-sans text-sm md:text-base leading-relaxed" data-id="element-165">
                  También conviví —y convivo— con esa duda constante de si
                  realmente es suficiente para hacer música. Esa sensación de
                  que siempre falta algo. Con el tiempo aprendí que esa duda no
                  se va comprando más equipos, sino ganando criterio y confianza
                  en lo que ya tenés.
                </p>

                <p className="text-gray-300 font-sans text-sm md:text-base leading-relaxed" data-id="element-166">
                  Hago canciones, grabo, produzco y sigo aprendiendo todos los
                  días. No desde un pedestal, sino desde el mismo lugar que vos:
                  el de alguien que quiere crear, terminar proyectos y disfrutar
                  el proceso sin que lo técnico sea un obstáculo.
                </p>

                <p className="text-gray-300 font-sans text-sm md:text-base leading-relaxed" data-id="element-167">
                  Además, soy director del{' '}
                  <span className="text-white font-semibold" data-id="element-168">
                    Instituto Musical Clave
                  </span>
                  , un espacio de formación musical por el que pasaron más de{' '}
                  <span className="text-[#FF8719] font-semibold" data-id="element-169">
                    10.000 alumnos
                  </span>{' '}
                  en más de 20 años. Esa experiencia me confirmó algo simple:
                  cuando la información es clara y aplicable, todos podemos
                  avanzar.
                </p>

                <p className="text-gray-300 font-sans text-sm md:text-base leading-relaxed" data-id="element-170">
                  Esta guía nace de ese recorrido. Para ayudarte a grabar con
                  más seguridad, menos dudas y más disfrute.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Bonuses Section */}
        <section id="bonuses" className="py-12 md:py-20 px-4 bg-[#1D1F22]" data-id="element-171">
          <div className="max-w-6xl mx-auto" data-id="element-172">
            <div className="text-center mb-8 md:mb-16" data-id="element-173">
              <span className="text-3xl md:text-4xl mb-2 block" data-id="element-174">🎁</span>
              <h2 className="text-2xl md:text-4xl text-white mb-2 md:mb-4" data-id="element-175">
                Te llevás la guía + 7 bonos prácticos
              </h2>
              <p className="text-base md:text-xl text-gray-400 max-w-2xl mx-auto font-sans" data-id="element-176">
                Bonos pensados para que no te trabes en el proceso y avances con
                seguridad.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-177">
              {bonuses.map(bonus => <motion.div key={bonus.id} whileHover={{
              scale: 1.02
            }} className="bg-[#2A2D31] p-4 md:p-6 rounded-xl border border-gray-800 shadow-lg hover:border-[#FF8719]/50 transition-all" data-id="element-178">
                  <div className="flex items-start justify-between mb-3 md:mb-4" data-id="element-179">
                    <div className="bg-[#1D1F22] p-2 md:p-3 rounded-lg border border-gray-700" data-id="element-180">
                      {bonus.icon}
                    </div>
                    <span className="bg-[#FF8719]/20 text-[#FF8719] text-xs font-bold px-2 py-1 rounded font-sans" data-id="element-181">
                      BONO {bonus.id}
                    </span>
                  </div>
                  <h4 className="text-base md:text-lg text-white mb-1 md:mb-2 font-sans font-bold" data-id="element-182">
                    {bonus.title}
                  </h4>
                  <p className="text-gray-400 text-xs md:text-sm mb-3 md:mb-4 font-sans" data-id="element-183">
                    {bonus.desc}
                  </p>
                  <div className="flex items-center justify-between border-t border-gray-700 pt-3 md:pt-4" data-id="element-184">
                    <span className="text-gray-500 line-through text-xs md:text-sm font-sans" data-id="element-185">
                      {bonus.price}
                    </span>
                    <span className="text-green-400 font-bold text-xs md:text-sm font-sans" data-id="element-186">
                      Incluido por $0
                    </span>
                  </div>
                </motion.div>)}
            </div>

            <div className="mt-8 md:mt-12 text-center" data-id="element-187">
              <div className="inline-flex items-center gap-2 bg-green-900/30 text-green-400 px-3 py-1.5 md:px-4 md:py-2 rounded-full font-medium border border-green-800 font-sans text-sm md:text-base" data-id="element-188">
                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5" data-id="element-189" />
                Incluye también la clase gratis de Ableton básico
              </div>
            </div>
          </div>
        </section>

        {/* Target Audience */}
        <section className="bg-[#2A2D31] py-12 md:py-20 px-4" data-id="element-190">
          <div className="max-w-5xl mx-auto" data-id="element-191">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-192">
              {/* For Who */}
              <div className="space-y-4 md:space-y-6" data-id="element-193">
                <h3 className="text-xl md:text-2xl text-green-400 flex items-center gap-2 font-sans font-bold" data-id="element-194">
                  <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8" data-id="element-195" /> Para quién
                  es
                </h3>
                <ul className="space-y-3 md:space-y-4 font-sans" data-id="element-196">
                  {['Si estás por armar tu home studio y no querés comprar a ciegas', 'Si grabás voces / guitarra / contenido y querés un setup simple', 'Si te abruma YouTube y querés una ruta clara'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-sm md:text-base" data-id="element-197">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2" data-id="element-198"></div>
                      <span data-id="element-199">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Not For Who */}
              <div className="space-y-4 md:space-y-6" data-id="element-200">
                <h3 className="text-xl md:text-2xl text-red-400 flex items-center gap-2 font-sans font-bold" data-id="element-201">
                  <XCircle className="w-6 h-6 md:w-8 md:h-8" data-id="element-202" /> No es para
                </h3>
                <ul className="space-y-3 md:space-y-4 font-sans" data-id="element-203">
                  {['Quien busca "el mejor estudio" antes de aprender a grabar', 'Quien quiere resultados sin practicar ni probar'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-sm md:text-base" data-id="element-204">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2" data-id="element-205"></div>
                      <span data-id="element-206">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-20 px-4 bg-[#1D1F22]" data-id="element-207">
          <div className="max-w-4xl mx-auto text-center" data-id="element-208">
            <span className="text-3xl md:text-4xl mb-2 block" data-id="element-209">💬</span>
            <h2 className="text-2xl md:text-4xl text-white mb-8 md:mb-12" data-id="element-210">
              Lo que suele pasar cuando la gente ordena el proceso
            </h2>

            <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-211">
              <div className="bg-[#2A2D31] p-5 md:p-8 rounded-2xl shadow-sm border border-gray-800 italic text-gray-300 relative" data-id="element-212">
                <div className="absolute -top-3 -left-3 md:-top-4 md:-left-4 text-5xl md:text-6xl text-[#FF8719]/20 font-serif" data-id="element-213">
                  "
                </div>
                <p className="mb-3 md:mb-4 relative z-10 font-sans text-sm md:text-base" data-id="element-214">
                  "Dejé de mirar 20 videos por día. Elegí el equipo con criterio
                  y grabé mi primera toma sin frustración."
                </p>
                <div className="font-bold text-white not-italic font-sans text-sm md:text-base" data-id="element-215">
                  — Juan P. (Músico)
                </div>
              </div>
              <div className="bg-[#2A2D31] p-5 md:p-8 rounded-2xl shadow-sm border border-gray-800 italic text-gray-300 relative" data-id="element-216">
                <div className="absolute -top-3 -left-3 md:-top-4 md:-left-4 text-5xl md:text-6xl text-[#FF8719]/20 font-serif" data-id="element-217">
                  "
                </div>
                <p className="mb-3 md:mb-4 relative z-10 font-sans text-sm md:text-base" data-id="element-218">
                  "No era 'comprar más': era configurar bien y entender el
                  flujo. Me cambió la cabeza."
                </p>
                <div className="font-bold text-white not-italic font-sans text-sm md:text-base" data-id="element-219">
                  — Sofía M. (Podcaster)
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing & CTA */}
        <section id="pricing" className="py-12 md:py-20 px-4 bg-[#1D1F22] relative" data-id="element-220">
          <div className="max-w-4xl mx-auto text-center" data-id="element-221">
            <div className="bg-gradient-to-b from-[#2A2D31] to-[#1D1F22] p-5 md:p-12 rounded-2xl md:rounded-3xl border border-gray-700 shadow-2xl relative overflow-hidden" data-id="element-222">
              <div className="absolute top-0 right-0 bg-[#FF8719] text-white px-3 py-0.5 md:px-4 md:py-1 rounded-bl-xl font-bold font-sans text-sm" data-id="element-223">
                -50% OFF
              </div>

              <h2 className="text-2xl md:text-4xl text-white mb-6 md:mb-8" data-id="element-224">
                Empezá hoy mismo
              </h2>

              <div className="flex flex-col items-center justify-center gap-1 md:gap-2 mb-6 md:mb-8" data-id="element-225">
                <span className="text-gray-500 text-xl md:text-2xl line-through font-sans" data-id="element-226">
                  $27.980 ARS
                </span>
                <div className="flex items-baseline gap-2" data-id="element-227">
                  <span className="text-4xl md:text-6xl font-extrabold text-[#FF8719] font-sans" data-id="element-228">
                    $13.990
                  </span>
                  <span className="text-lg md:text-xl text-gray-400 font-medium font-sans" data-id="element-229">
                    ARS
                  </span>
                </div>
                <span className="bg-green-900/30 text-green-400 px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-bold mt-1 md:mt-2 border border-green-800 font-sans" data-id="element-230">
                  🔥 Oferta por tiempo limitado
                </span>
              </div>

              <a href="https://www.institutoclave.com/cart/47548550971610:1" target="_blank" rel="noopener noreferrer" className="w-full max-w-md bg-[#FF8719] hover:bg-orange-600 text-white text-lg md:text-xl font-bold py-3 md:py-4 px-6 md:px-8 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 md:gap-3 mb-4 md:mb-6 group mx-auto font-sans" data-id="element-231">
                <Download className="w-5 h-5 md:w-6 md:h-6 group-hover:animate-bounce" data-id="element-232" />
                👇 Obtener acceso ahora 👇
              </a>

              {/* Checklist of what's included */}
              <div className="bg-[#1D1F22] rounded-xl p-4 md:p-6 mb-4 md:mb-6 border border-gray-700 max-w-lg mx-auto" data-id="element-233">
                <p className="text-white font-bold mb-3 md:mb-4 font-sans text-base md:text-lg" data-id="element-234">
                  ✅ Tu acceso incluye:
                </p>
                <ul className="space-y-2 md:space-y-3 text-left font-sans" data-id="element-235">
                  {/* Main products */}
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-236">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-green-400 flex-shrink-0" data-id="element-237" />
                    <span data-id="element-238">
                      📘 <strong data-id="element-239">¿Cómo armar tu home studio desde cero?</strong>{' '}
                      (Guía PDF)
                    </span>
                  </li>

                  {/* Divider */}
                  <li className="border-t border-gray-700 pt-2 md:pt-3 mt-2 md:mt-3" data-id="element-240">
                    <span className="text-[#FF8719] font-bold text-xs uppercase tracking-wider" data-id="element-241">
                      + 7 Bonos Exclusivos:
                    </span>
                  </li>

                  {/* All 7 bonuses */}
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-242">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-[#FF8719] flex-shrink-0" data-id="element-243" />
                    <span data-id="element-244">
                      Bono 1 – Checklist de compra por presupuesto (USD)
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-245">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-[#FF8719] flex-shrink-0" data-id="element-246" />
                    <span data-id="element-247">Bono 2 – Guía de errores técnicos caros</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-248">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-[#FF8719] flex-shrink-0" data-id="element-249" />
                    <span data-id="element-250">Bono 3 – Checklist para elegir tu computadora</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-251">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-[#FF8719] flex-shrink-0" data-id="element-252" />
                    <span data-id="element-253">Bono 4 – Glosario completo de Home Studio</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-254">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-[#FF8719] flex-shrink-0" data-id="element-255" />
                    <span data-id="element-256">
                      Bono 5 – Aspectos Generales de Ableton Live (Video)
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-257">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-[#FF8719] flex-shrink-0" data-id="element-258" />
                    <span data-id="element-259">Bono 6 – Guía práctica de frecuencias</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-260">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-[#FF8719] flex-shrink-0" data-id="element-261" />
                    <span data-id="element-262">Bono 7 – Pensar como productor</span>
                  </li>

                  {/* Divider */}
                  <li className="border-t border-gray-700 pt-2 md:pt-3 mt-2 md:mt-3" data-id="element-263">
                    <span className="text-green-400 font-bold text-xs uppercase tracking-wider" data-id="element-264">
                      + Beneficios:
                    </span>
                  </li>

                  {/* Benefits */}
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-265">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-green-400 flex-shrink-0" data-id="element-266" />
                    <span data-id="element-267">Acceso inmediato y de por vida</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-268">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-green-400 flex-shrink-0" data-id="element-269" />
                    <span data-id="element-270">Actualizaciones gratuitas</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-300 text-xs md:text-sm" data-id="element-271">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-green-400 flex-shrink-0" data-id="element-272" />
                    <span data-id="element-273">Soporte por email</span>
                  </li>
                </ul>
              </div>

              {/* Remaining spots */}
              <div className="bg-green-900/30 border border-green-800 rounded-lg px-3 py-2 md:px-4 md:py-3 mb-4 md:mb-6 max-w-md mx-auto" data-id="element-274">
                <p className="text-green-400 font-bold text-xs md:text-sm font-sans" data-id="element-275">
                  ✅ Más de <span className="text-white" data-id="element-276">10.000 alumnos</span>{' '}
                  pasaron por Instituto Clave en 20 años.
                </p>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-gray-400 font-sans" data-id="element-277">
                <div className="flex items-center gap-2" data-id="element-278">
                  <CreditCard className="w-4 h-4" data-id="element-279" />
                  <span data-id="element-280">Pago simple • Acceso inmediato</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-281">
                  <ShieldCheck className="w-4 h-4" data-id="element-282" />
                  <span data-id="element-283">Garantía de 7 días</span>
                </div>
              </div>

              <p className="mt-4 md:mt-6 text-xs text-gray-500 font-sans" data-id="element-284">
                *Vas a recibir el enlace de acceso por email. Revisá
                "Promociones/Spam" si no aparece en 5 minutos.
              </p>
            </div>

            {/* Payment Security Badge */}
            <div className="mt-6 md:mt-8 bg-[#1D1F22] rounded-2xl p-4 md:p-6 border border-gray-700 max-w-md mx-auto" data-id="element-285">
              <div className="flex items-center justify-center gap-2 text-gray-400 mb-3 md:mb-4 font-sans" data-id="element-286">
                <ShieldCheck className="w-4 h-4 md:w-5 md:h-5" data-id="element-287" />
                <span className="text-xs md:text-sm" data-id="element-288">
                  Pago 100% seguro procesado por:
                </span>
              </div>
              <div className="bg-[#2A2D31] rounded-xl p-3 md:p-4 flex items-center justify-center" data-id="element-289">
                <img src="https://cdn.magicpatterns.com/uploads/vj1Ar4wr8zj6jW1zvMFz8P/MP_RGB_HANDSHAKE_pluma_horizontal.png" alt="Mercado Pago" className="h-16 md:h-20 w-auto" data-id="element-290" />
              </div>
              <div className="flex items-center justify-center gap-4 md:gap-6 mt-3 md:mt-4 text-xs text-gray-500 font-sans" data-id="element-291">
                <span className="flex items-center gap-1" data-id="element-292">
                  <ShieldCheck className="w-3 h-3 md:w-4 md:h-4" data-id="element-293" /> Garantía 7
                  días
                </span>
                <span className="flex items-center gap-1" data-id="element-294">
                  <Download className="w-3 h-3 md:w-4 md:h-4" data-id="element-295" /> Acceso
                  inmediato
                </span>
                <span className="flex items-center gap-1" data-id="element-296">
                  <Users className="w-3 h-3 md:w-4 md:h-4" data-id="element-297" /> +5,000 confían
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* How to Buy */}
        <section className="bg-[#2A2D31] py-12 md:py-20 px-4" data-id="element-298">
          <div className="max-w-4xl mx-auto" data-id="element-299">
            <h2 className="text-2xl md:text-3xl text-center text-white mb-8 md:mb-12" data-id="element-300">
              🛒 Cómo comprar (simple)
            </h2>

            <div className="grid md:grid-cols-3 gap-4 md:gap-8" data-id="element-301">
              {[{
              step: '1',
              text: 'Tocá el botón de compra y completá el pago.',
              icon: <CreditCard className="w-5 h-5 md:w-6 md:h-6 text-[#FF8719]" data-id="element-302" />
            }, {
              step: '2',
              text: 'Te llega un email con el acceso a la guía + bonos.',
              icon: <Mail className="w-5 h-5 md:w-6 md:h-6 text-[#FF8719]" data-id="element-303" />
            }, {
              step: '3',
              text: 'Empezás con el paso 1 y en 7 días tenés el setup funcionando.',
              icon: <PlayCircle className="w-5 h-5 md:w-6 md:h-6 text-[#FF8719]" data-id="element-304" />
            }].map((item, i) => <div key={i} className="bg-[#1D1F22] p-4 md:p-6 rounded-xl shadow-sm border border-gray-800 text-center relative" data-id="element-305">
                  <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2 bg-[#FF8719] text-white w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold border-4 border-[#1D1F22] font-sans text-sm" data-id="element-306">
                    {item.step}
                  </div>
                  <div className="mt-3 md:mt-4 mb-3 md:mb-4 flex justify-center" data-id="element-307">
                    {item.icon}
                  </div>
                  <p className="text-gray-300 font-medium font-sans text-sm md:text-base" data-id="element-308">
                    {item.text}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-20 px-4 bg-[#1D1F22]" data-id="element-309">
          <div className="max-w-3xl mx-auto" data-id="element-310">
            <h2 className="text-2xl md:text-3xl text-center text-white mb-8 md:mb-12" data-id="element-311">
              ❓ Preguntas frecuentes
            </h2>
            <div className="space-y-1 md:space-y-2" data-id="element-312">
              {faqs.map((faq, i) => <FAQItem key={i} question={faq.question} answer={faq.answer} data-id="element-313" />)}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F1012] text-gray-500 py-8 md:py-12 pb-24 md:pb-28 text-center text-sm" data-id="element-314">
        <div className="max-w-6xl mx-auto px-4" data-id="element-315">
          <div className="flex items-center justify-center mb-4 md:mb-6" data-id="element-316">
            <img src="https://cdn.magicpatterns.com/uploads/bZAhMuCuYJ3qHXrJ73mCqQ/Logo_sin_fondo.png" alt="Instituto Musical Clave" className="h-16 md:h-20 w-auto" data-id="element-317" />
          </div>

          {/* Social Media Links */}
          <div className="flex items-center justify-center gap-4 mb-4 md:mb-6" data-id="element-318">
            <a href="https://www.facebook.com/institutomusicalclave" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-[#FF8719] p-2.5 rounded-full transition-all duration-300 group" data-id="element-319">
              <Facebook className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" data-id="element-320" />
            </a>
            <a href="http://www.instagram.com/institutomusicalclave" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-[#FF8719] p-2.5 rounded-full transition-all duration-300 group" data-id="element-321">
              <Instagram className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" data-id="element-322" />
            </a>
          </div>

          <p className="font-sans text-gray-400 text-xs md:text-sm" data-id="element-323">
            © 2026 Instituto Musical Clave. Todos los derechos reservados.
          </p>
          <p className="mt-1 md:mt-2 font-sans text-gray-500 text-xs md:text-sm" data-id="element-324">
            Más de 20 años creando cultura Argentina 🇦🇷
          </p>
        </div>
      </footer>

      {/* Purchase Notification Popup */}
      <PurchaseNotification data-id="element-325" />

      {/* WhatsApp Floating Button */}
      <a href="https://wa.me/5492613617426?text=Hola!%20Quiero%20comprar%20la%20guía%20Home%20Studio%20con%20el%2050%25%20de%20descuento.%20Cómo%20accedo?" target="_blank" rel="noopener noreferrer" className="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-40" data-id="element-326">
        <motion.div animate={{
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="bg-[#25D366] hover:bg-[#20bd5a] w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow cursor-pointer" data-id="element-327">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-7 h-7" data-id="element-328">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-329" />
          </svg>
        </motion.div>
      </a>
    </div>;
}